export type RoomStatus = 'Active' | 'Under Maintenance' | 'Archived';

export interface Room {
  id: number;
  room_number: string; // e.g. "A-101"
  floor: string;
  capacity: number;
  equipment: string[]; // e.g. ['Projector', 'Whiteboard', 'PCs', 'WiFi']
  status: RoomStatus;
  dos_idf?: number;
  dos_code?: string;
  dos_lib?: string;
  user_lib?: string;
  last_updt?: string;
}

export type TeacherGender = 'male' | 'female';
export type TeacherRateType = 'hourly' | 'daily' | 'monthly' | 'percentage' | 'fixed';

export interface Teacher {
  id: number;
  first_name: string;
  last_name: string;
  full_name: string;
  gender?: TeacherGender;
  date_of_birth?: string;
  email: string;
  phone: string;
  specialization: string;
  rate_type?: TeacherRateType;
  hourly_rate: number;
  hire_date?: string;
  is_active: boolean;
  bio?: string;
  avatar_url?: string;
  photo_base64?: string;
  photo_mime_type?: string;
  cv_file_name?: string;
  cv_file_base64?: string;
  cv_file_mime_type?: string;
  dos_idf?: number;
  dos_code?: string;
  dos_lib?: string;
  user_lib?: string;
  last_updt?: string;
  portal_active?: boolean;
  portal_password?: string;
  can_take_attendance?: boolean;
}

export type StudentCategory = 'adult' | 'junior';
export type StudentGender = 'male' | 'female';
export type BloodGroup = 'A-' | 'A+' | 'B-' | 'B+' | 'AB-' | 'AB+' | 'O-' | 'O+';
export type GuardianRelation =
  | 'parent'
  | 'sibling'
  | 'grandparent'
  | 'uncle_aunt'
  | 'guardian'
  | 'other';
export type HealthCondition = 'asthma' | 'diabetes' | 'epilepsy' | 'other';
export type SchoolLevel =
  | 'primary_1'
  | 'primary_2'
  | 'primary_3'
  | 'primary_4'
  | 'primary_5'
  | 'middle_1'
  | 'middle_2'
  | 'middle_3'
  | 'middle_4';


export interface Student {
  id: number;
  uid?: string;
  STD_UIDF?: string;
  code?: string;
  STD_CODE?: string;
  // --- Common ---
  category: StudentCategory;         // 'adult' | 'junior'
  first_name: string;
  last_name: string;
  gender?: StudentGender;
  date_of_birth: string;
  address?: string;
  email?: string;
  phone?: string;
  emergency_contact: string;
  financial_hold?: boolean;
  hold_since?: string;

  // Photo & attachment (stored as base64 in DB)
  photo_base64?: string;             // data:image/...;base64,...
  photo_mime_type?: string;          // e.g. 'image/jpeg'
  attached_file_name?: string;       // original filename for display
  attached_file_base64?: string;     // base64 encoded file content
  attached_file_mime_type?: string;  // e.g. 'application/pdf'

  // --- Adult-only ---
  profession?: string;               // ex: étudiant, ingénieur...

  // --- Junior-only ---
  school_level?: SchoolLevel;
  has_health_issues?: boolean;
  health_conditions?: HealthCondition[];
  health_other_detail?: string;        // free-text when 'Autres' is checked
  has_allergies?: boolean;
  allergy_details?: string;            // free-text when has_allergies = true
  blood_group?: BloodGroup;
  guardian_first_name?: string;
  guardian_last_name?: string;
  guardian_relation?: GuardianRelation;
  guardian_phone?: string;
  guardian_email?: string;
  authorized_pickup?: string;        // multiline: Nom & prénom + Lien
  image_right?: boolean;             // Droit à l'image: Oui/Non
  registration_status?: string;      // 'Pre-Registered' | 'Active' | 'Refused'

  // --- Pre-registration (from public link) ---
  crs_idf?: number;
  crs_title?: string;        // CRS_TITLE – read-only, from LEFT JOIN TABP_ACADEMY_COURSE
  time_preference?: string;
  loi_18_07_consent?: boolean;
  loi_18_07_consent_date?: string;

  // --- Discount Affectation ---
  discount_id?: number;      // DSC_IDF
  discount_code?: string;    // DSC_CODE
  discount_name?: string;    // DSC_NAME

  // --- Dossier (from LEFT JOIN TABP_DOSSIER) ---
  dos_idf?: number;                  // FK stored in student row
  dos_code?: string;                 // read-only, display only
  dos_lib?: string;                  // read-only, display only

  // --- Audit display (from LEFT JOIN TABP_SYS_USER) ---
  last_updt?: string;
  submitted_date?: string;           // YYYY-MM-DD for dashboard pre-reg filtering
  user_lib?: string;                 // last modifier's name

  // --- Portal Access ---
  portal_active?: boolean;
  portal_password?: string;
}

export interface CourseTemplate {
  id: number;
  crs_uidf?: string;   // CRS_UIDF – server-generated UUID
  title: string;       // CRS_TITLE
  total_hours: number; // CRS_TOTAL_HOURS
  price: number;       // CRS_PRICE
  show_price?: boolean;// CRS_SHOW_PRICE
  description: string; // CRS_DESCRIPTION
  start_date?: string; // CRS_START_DATE (YYYY-MM-DD)
  end_date?: string;   // CRS_END_DATE   (YYYY-MM-DD)
  age_band?: string;   // CRS_AGE_BAND
  age_min?: number;    // CRS_AGE_MIN
  age_max?: number;    // CRS_AGE_MAX
  dos_idf?: number;    // DOS_IDF
  dos_code?: string;   // DOS_CODE (read-only, from join)
  dos_lib?: string;    // DOS_LIB  (read-only, from join)
  section_dossier?: string;
  user_idf?: number;   // USER_IDF (FK to TABP_SYS_USER, set by server)
  user_lib?: string;   // USER_LIB (read-only, from join)
  last_updt?: string;  // LAST_UPDT (set by server)
}

export type GroupStatus = 'Upcoming' | 'Ongoing' | 'Completed';

export interface Group {
  id: number;
  name: string; // e.g. "Fullstack Web - Sept 2026"
  course_id: number;
  teacher_id: number;
  max_capacity: number;
  start_date: string;
  expected_end_date: string;
  status: GroupStatus;
  dos_idf?: number;
  dos_code?: string;
  dos_lib?: string;
  section_dossier?: string;
  user_lib?: string;
  last_updt?: string;
}

export type EnrollmentStatus = 'Enrolled' | 'Dropped' | 'Completed';

export interface GroupStudent {
  id: number;
  group_id: number;
  student_id: number;
  enrollment_date: string;
  status: EnrollmentStatus;
}

export interface Session {
  id: number;
  group_id: number;
  room_id: number;
  teacher_id: number;
  day_of_week: number; // dérivé de start_hour (colonne supprimée en base)
  // boubaker 22/08/2026 - nouvelles colonnes serveur (datetime complets)
  start_hour: string;  // ISO local "YYYY-MM-DDTHH:mm:ss" (SSN_START_HOUR)
  end_hour: string;    // ISO local (SSN_END_HOUR)
  duration: number;    // minutes (SSN_DURATION)
  // Champs dérivés pour compatibilité affichage (calculés depuis start/end_hour)
  start_time: string; // "09:00"
  end_time: string; // "11:00"
  date: string; // YYYY-MM-DD
  is_recurring?: boolean; // concept création uniquement (non persisté)
  repeat_weeks?: number;  // paramètre de duplication serveur (requête uniquement)
  parent_session_id?: number;
  attendance_taken?: boolean;
  status?: string; // 'Scheduled' | 'Cancelled' | 'Postponed' | 'Completed'
  status_reason?: string;
  postponed_date?: string;
  postponed_time?: string;
  teacher_absent?: boolean;
}

export type AttendanceStatus = 'Present' | 'Absent' | 'Late' | 'Excused';

export interface Attendance {
  id: number;
  session_id: number;
  student_id: number;
  status: AttendanceStatus;
  check_in_time?: string;
  justified?: boolean;
  justification_reason?: string;
  justification_doc_name?: string;
  justification_doc_base64?: string;
  justification_date?: string;
}

export type InvoiceStatus = 'Unpaid' | 'Partial' | 'Paid';

export interface Invoice {
  id: number;
  student_id: number;
  group_id?: number;
  invoice_number: string; // Unique
  total_amount: number;
  amount_paid: number;
  issue_date: string;
  due_date: string;
  status: InvoiceStatus;

  // Agency context
  dos_idf?: number;
  dos_code?: string;

  // Discount & Approvals
  discount_id?: number;
  discount_code?: string;
  discount_percent?: number;
  discount_amount?: number;
  original_amount?: number;
  discount_reason?: string;
  discount_approved?: boolean;
  discount_approved_by?: string;
}

export type PaymentMethod = 'Cash' | 'Card' | 'Transfer';

export interface Payment {
  id: number;
  invoice_id: number;
  student_id: number;
  amount: number;
  payment_date: string;
  method: PaymentMethod;
  receipt_number: string;
}

export interface Installment {
  id: number;
  invoice_id: number;
  installment_number: number;
  amount: number;
  due_date: string;
  status: 'Pending' | 'Paid';
}

export type QuizStatus = 'Draft' | 'Active' | 'Archived';

export type QuizDisplay = 'list' | 'assistant';

export interface Quiz {
  id: number;
  code: string;            // QIZ_CODE (max 12)
  title: string;           // QIZ_TITLE
  description: string;     // QIZ_DESCRIPTION
  duration_minutes: number;// QIZ_DURATION_MINUTES
  expiry_days: number;     // QIZ_EXPIRY_DAYS
  whatsapp?: string;       // QIZ_WHATSAPP
  register_link?: string;  // QIZ_REGISTER_LINK
  display: QuizDisplay;    // QIZ_DISPLAY
  auto_next?: boolean;     // QIZ_AUTO_NEXT
  QIZ_AUTO_NEXT?: boolean;
  max_attempts_link?: number; // QIZ_MAX_ATTEMPTS_LINK
  max_attempts_user?: number; // QIZ_MAX_ATTEMPTS_USER
  QIZ_MAX_ATTEMPTS_LINK?: number;
  QIZ_MAX_ATTEMPTS_USER?: number;
  status: QuizStatus;      // QIZ_STATUS
  start_date?: string;     // QIZ_START_DATE
  end_date?: string;       // QIZ_END_DATE
  dos_idf?: number;        // DOS_IDF
  dos_code?: string;
  dos_lib?: string;
  user_lib?: string;
  last_updt?: string;
}

export interface AgencyInfo {
  DOS_LIB?: string;
  DOS_EMAIL?: string;
  DOS_TELPH?: string;
  DOS_ADR1?: string;
  DOS_LOGO?: string;
}

export interface QuizPublic {
  QIZ_IDF: number;
  QIZ_CODE: string;
  QIZ_TITLE: string;
  QIZ_DESCRIPTION: string;
  QIZ_DURATION_MINUTES: number;
  QIZ_EXPIRY_DAYS: number;
  QIZ_WHATSAPP?: string;
  QIZ_REGISTER_LINK?: string;
  QIZ_DISPLAY?: QuizDisplay;
  QIZ_AUTO_NEXT?: boolean;
  QIZ_MAX_ATTEMPTS_LINK?: number;
  QIZ_MAX_ATTEMPTS_USER?: number;
  AGENCY?: AgencyInfo;
  QUESTIONS: Array<{
    QST_IDF: number;
    QST_TEXT: string;
    QST_TYPE: QuizQuestionType;
    QST_ORDER: number;
    QST_POINTS: number;
    OPTIONS: Array<{ key: string; label: string; order?: number }>;
  }>;
  LEVELS: Array<{
    LVL_CODE: string;
    LVL_TITLE: string;
    LVL_MIN_SCORE: number;
    LVL_MAX_SCORE: number;
    LVL_MESSAGE: string;
  }>;
}

export type QuizQuestionType = 'single' | 'multi' | 'boolean' | 'text';

export interface QuizQuestionOption {
  key: string;         // QOP_KEY
  label: string;       // QOP_LABEL
  order?: number;      // QOP_ORDER
  isCorrect?: boolean; // QOP_IS_CORRECT
}

export interface QuizQuestion {
  id: number;
  quiz_id: number;         // QIZ_IDF
  text: string;            // QST_TEXT
  type: QuizQuestionType;  // QST_TYPE
  options: QuizQuestionOption[]; // stored in TABP_ACADEMY_QUIZ_QUESTION_OPTIONS
  order: number;           // QST_ORDER
  points: number;          // QST_POINTS
}

export interface QuizLevel {
  id: number;
  quiz_id: number;         // QIZ_IDF
  code: string;            // LVL_CODE
  title: string;           // LVL_TITLE
  min_score: number;       // LVL_MIN_SCORE
  max_score: number;       // LVL_MAX_SCORE
  message: string;         // LVL_MESSAGE
}

export interface QuizAttempt {
  id: number;
  quiz_id: number;
  quiz_title?: string;     // QIZ_TITLE (read-only, join)
  full_name: string;       // ATT_FULL_NAME
  phone: string;
  email: string;
  age_band: string;
  score: number;
  total: number;
  level_code: string;
  answers: string;
  ref_code: string;
  duration_seconds?: number;
  ip_address?: string;     // ATT_IP_ADDRESS
  dos_idf?: number;
  last_updt?: string;
}

export interface AcademyParameter {
  id: number;              // PRM_IDF
  prm_uidf?: string;       // PRM_UIDF
  dos_idf: number;         // DOS_IDF
  dos_code?: string;       // DOS_CODE
  dos_lib?: string;        // DOS_LIB
  code: string;            // PRM_CODE
  value: string;           // PRM_VALUE
  lib?: string;            // PRM_LIB
  type?: string;           // PRM_TYPE
  user_idf?: number;       // USER_IDF
  user_lib?: string;       // USER_LIB
  last_updt?: string;      // LAST_UPDT
}

export interface PlanningControl {
  id: number;              // CTL_IDF
  ctl_uidf?: string;
  dos_idf?: number;
  code: string;            // CTL_CODE
  lib: string;             // CTL_LIB
  is_active: boolean;      // CTL_IS_ACTIVE
  is_blocking: boolean;    // CTL_IS_BLOCKING
  description?: string;    // CTL_DESCRIPTION
  user_lib?: string;
  last_updt?: string;
}

export type ValidatorRole = 'Planificateur' | 'Auto' | 'Admin';

export interface AcademyDiscount {
  id: number;              // DSC_IDF
  dsc_uidf?: string;
  dos_idf?: number;
  code: string;            // DSC_CODE (PACK, FAM, BLOB, FID, REF, GRP, PART, DIR)
  name: string;            // DSC_NAME
  percentage: number;      // DSC_PERCENTAGE
  requires_validation: boolean; // DSC_REQUIRES_VALIDATION
  validator_role: ValidatorRole; // DSC_VALIDATOR_ROLE
  is_auto_applied: boolean; // DSC_IS_AUTO_APPLIED
  min_courses?: number;    // DSC_MIN_COURSES
  description?: string;    // DSC_DESCRIPTION
  is_active: boolean;      // DSC_IS_ACTIVE
  assigned_students_count?: number;
  used_invoices_count?: number;
  user_lib?: string;
  last_updt?: string;
}

export type DiscountRequestStatus = 'Pending' | 'Approved' | 'Rejected';

export interface DiscountRequest {
  id: number;              // REQ_IDF
  req_uidf?: string;       // REQ_UIDF
  dos_idf: number;         // DOS_IDF
  dos_code?: string;
  dos_lib?: string;
  invoice_id: number;      // INV_IDF
  invoice_number?: string; // INV_NUMBER
  student_id: number;      // STD_IDF
  student_name?: string;   // STD_NAME
  student_email?: string;  // STD_EMAIL
  student_phone?: string;  // STD_PHONE
  discount_id?: number;    // DSC_IDF
  discount_code?: string;  // REQ_DISCOUNT_CODE
  discount_percent: number;// REQ_DISCOUNT_PERCENT
  discount_amount: number; // REQ_DISCOUNT_AMOUNT
  original_amount: number; // REQ_ORIGINAL_AMOUNT
  new_amount: number;      // REQ_NEW_AMOUNT
  justification: string;   // REQ_JUSTIFICATION
  status: DiscountRequestStatus; // REQ_STATUS
  requested_by_user_id?: number; // REQUESTED_BY_USER_IDF
  requested_by_name?: string;    // REQUESTED_BY_NAME
  request_date: string;    // REQUEST_DATE
  reviewed_by_user_id?: number;  // REVIEWED_BY_USER_IDF
  reviewed_by_name?: string;     // REVIEWED_BY_NAME
  review_date?: string;    // REVIEW_DATE
  review_comment?: string; // REVIEW_COMMENT
  last_updt?: string;
}

export type RoomRentalStatus = 'CONFIRMED' | 'PENDING' | 'CANCELLED' | 'COMPLETED';

export interface RoomRental {
  id: number;              // RNT_IDF
  rnt_uidf?: string;       // RNT_UIDF
  dos_idf?: number;        // DOS_IDF
  dos_code?: string;       // DOS_CODE
  dos_lib?: string;        // DOS_LIB
  arm_idf: number;         // ARM_IDF
  room_number?: string;    // ARM_ROOM_NUMBER
  room_capacity?: number;  // ARM_CAPACITY
  code: string;            // RNT_CODE
  lib: string;             // RNT_LIB
  client_name: string;     // RNT_CLIENT_NAME
  client_phone?: string;   // RNT_CLIENT_PHONE
  client_email?: string;   // RNT_CLIENT_EMAIL
  start_date: string;      // RNT_START_DATE
  end_date: string;        // RNT_END_DATE
  price: number;           // RNT_PRICE
  deposit: number;         // RNT_DEPOSIT
  status: RoomRentalStatus;// RNT_STATUS
  notes?: string;          // RNT_NOTES
  user_idf?: number;       // USER_IDF
  user_lib?: string;       // USER_LIB
  last_updt?: string;      // LAST_UPDT
}

export type EmailTemplateMailType =
  | 'DEBT_REMINDER_ADULT'
  | 'DEBT_REMINDER_KIDS'
  | 'AUTH_OTP'
  | 'CHANGE_PASSWORD'
  | 'RESET_PASSWORD'
  | 'DISCOUNT_REQUEST_SUBMITTED'
  | 'DISCOUNT_REQUEST_APPROVED'
  | 'DISCOUNT_REQUEST_REJECTED'
  | 'STUDENT_PRE_REG_ACCEPTED'
  | 'STUDENT_PRE_REG_REFUSED'
  | 'PAYMENT_RECEIPT'
  | 'GENERAL'
  | string;

export interface EmailTemplate {
  id: number;              // TPL_IDF
  uid?: string;            // TPL_UIDF
  dos_idf?: number;        // DOS_IDF
  dos_code?: string;       // DOS_CODE
  dos_lib?: string;        // DOS_LIB
  code: string;            // TPL_CODE
  mail_type: string;       // TPL_MAIL_TYPE
  lang: string;            // TPL_LANG ('fr' | 'ar' | 'en')
  name: string;            // TPL_NAME
  subject: string;         // TPL_SUBJECT
  body: string;            // TPL_BODY
  tags?: string;           // TPL_TAGS
  is_active: boolean;      // TPL_IS_ACTIVE
  description?: string;    // TPL_DESCRIPTION
  last_updt?: string;      // LAST_UPDT
  user_idf?: number;       // USER_IDF
  user_lib?: string;       // USER_LIB
}

export const STANDARD_EMAIL_TAGS: Record<string, { tag: string; labelFr: string; labelEn: string; labelAr: string }[]> = {
  DEBT_REMINDER_ADULT: [
    { tag: '{agencyName}', labelFr: 'Nom Agence', labelEn: 'Agency Name', labelAr: 'اسم الوكالة' },
    { tag: '{agencyPhone}', labelFr: 'Téléphone Agence', labelEn: 'Agency Phone', labelAr: 'هاتف الوكالة' },
    { tag: '{agencyEmail}', labelFr: 'Email Agence', labelEn: 'Agency Email', labelAr: 'البريد الإلكتروني للوكالة' },
    { tag: '{agencyAddress}', labelFr: 'Adresse Agence', labelEn: 'Agency Address', labelAr: 'عنوان الوكالة' },
    { tag: '{student.fullName}', labelFr: 'Nom Complet Apprenant', labelEn: 'Student Full Name', labelAr: 'الاسم الكامل للتلميذ' },
    { tag: '{student.code}', labelFr: 'Code Apprenant', labelEn: 'Student Code', labelAr: 'رمز التلميذ' },
    { tag: '{student.email}', labelFr: 'Email Apprenant', labelEn: 'Student Email', labelAr: 'بريد التلميذ' },
    { tag: '{student.phone}', labelFr: 'Tél Apprenant', labelEn: 'Student Phone', labelAr: 'هاتف التلميذ' },
    { tag: '{invoices.table}', labelFr: 'Tableau Factures Impayées', labelEn: 'Unpaid Invoices Table', labelAr: 'جدول الفواتير غير المسددة' },
    { tag: '{totalBalanceDue}', labelFr: 'Total Solde Dû', labelEn: 'Total Balance Due', labelAr: 'المجموع الإجمالي المستحق' },
    { tag: '{currency}', labelFr: 'Devise', labelEn: 'Currency', labelAr: 'العملة' },
  ],
  DEBT_REMINDER_KIDS: [
    { tag: '{agencyName}', labelFr: 'Nom Agence', labelEn: 'Agency Name', labelAr: 'اسم الوكالة' },
    { tag: '{agencyPhone}', labelFr: 'Téléphone Agence', labelEn: 'Agency Phone', labelAr: 'هاتف الوكالة' },
    { tag: '{agencyEmail}', labelFr: 'Email Agence', labelEn: 'Agency Email', labelAr: 'البريد الإلكتروني للوكالة' },
    { tag: '{agencyAddress}', labelFr: 'Adresse Agence', labelEn: 'Agency Address', labelAr: 'عنوان الوكالة' },
    { tag: '{guardian.fullName}', labelFr: 'Nom Tuteur / Parent', labelEn: 'Guardian Full Name', labelAr: 'اسم الولي' },
    { tag: '{guardian.relation}', labelFr: 'Lien de Parenté', labelEn: 'Relation', labelAr: 'صلة القرابة' },
    { tag: '{student.fullName}', labelFr: 'Nom(s) des Enfants', labelEn: 'Children Full Name(s)', labelAr: 'اسم التلميذ / الأبناء' },
    { tag: '{student.code}', labelFr: 'Code Apprenant', labelEn: 'Student Code', labelAr: 'رمز التلميذ' },
    { tag: '{invoices.table}', labelFr: 'Tableau Factures Impayées', labelEn: 'Unpaid Invoices Table', labelAr: 'جدول الفواتير غير المسددة' },
    { tag: '{totalBalanceDue}', labelFr: 'Total Solde Dû', labelEn: 'Total Balance Due', labelAr: 'المجموع الإجمالي المستحق' },
    { tag: '{currency}', labelFr: 'Devise', labelEn: 'Currency', labelAr: 'العملة' },
  ],
  AUTH_OTP: [
    { tag: '{otp}', labelFr: 'Code OTP', labelEn: 'OTP Code', labelAr: 'رمز التحقق' },
    { tag: '{expirationMinutes}', labelFr: 'Durée Validité (min)', labelEn: 'Expiry Minutes', labelAr: 'مدة الصلاحية' },
    { tag: '{userName}', labelFr: 'Nom Utilisateur', labelEn: 'User Name', labelAr: 'اسم المستخدم' },
    { tag: '{agencyName}', labelFr: 'Nom Agence', labelEn: 'Agency Name', labelAr: 'اسم الوكالة' },
    { tag: '{appName}', labelFr: 'Nom Application', labelEn: 'App Name', labelAr: 'اسم التطبيق' },
  ],
  CHANGE_PASSWORD: [
    { tag: '{otp}', labelFr: 'Code OTP', labelEn: 'OTP Code', labelAr: 'رمز التحقق' },
    { tag: '{expirationMinutes}', labelFr: 'Durée Validité (min)', labelEn: 'Expiry Minutes', labelAr: 'مدة الصلاحية' },
    { tag: '{userName}', labelFr: 'Nom Utilisateur', labelEn: 'User Name', labelAr: 'اسم المستخدم' },
    { tag: '{agencyName}', labelFr: 'Nom Agence', labelEn: 'Agency Name', labelAr: 'اسم الوكالة' },
    { tag: '{appName}', labelFr: 'Nom Application', labelEn: 'App Name', labelAr: 'اسم التطبيق' },
  ],
  RESET_PASSWORD: [
    { tag: '{otp}', labelFr: 'Code OTP', labelEn: 'OTP Code', labelAr: 'رمز التحقق' },
    { tag: '{expirationMinutes}', labelFr: 'Durée Validité (min)', labelEn: 'Expiry Minutes', labelAr: 'مدة الصلاحية' },
    { tag: '{userName}', labelFr: 'Nom Utilisateur', labelEn: 'User Name', labelAr: 'اسم المستخدم' },
    { tag: '{agencyName}', labelFr: 'Nom Agence', labelEn: 'Agency Name', labelAr: 'اسم الوكالة' },
    { tag: '{appName}', labelFr: 'Nom Application', labelEn: 'App Name', labelAr: 'اسم التطبيق' },
  ],
  DISCOUNT_REQUEST_SUBMITTED: [
    { tag: '{agencyName}', labelFr: 'Nom Agence', labelEn: 'Agency Name', labelAr: 'اسم الوكالة' },
    { tag: '{invoiceNumber}', labelFr: 'N° Facture', labelEn: 'Invoice Number', labelAr: 'رقم الفاتورة' },
    { tag: '{student.fullName}', labelFr: 'Nom Apprenant', labelEn: 'Student Full Name', labelAr: 'اسم التلميذ' },
    { tag: '{discountName}', labelFr: 'Nom Remise', labelEn: 'Discount Name', labelAr: 'اسم التخفيض' },
    { tag: '{discountPercent}', labelFr: 'Pourcentage Remise', labelEn: 'Discount Percent', labelAr: 'نسبة التخفيض' },
    { tag: '{originalAmount}', labelFr: 'Montant Initial', labelEn: 'Original Amount', labelAr: 'المبلغ الأصلي' },
    { tag: '{newAmount}', labelFr: 'Nouveau Montant', labelEn: 'New Amount', labelAr: 'المبلغ الجديد' },
    { tag: '{justification}', labelFr: 'Justification', labelEn: 'Justification', labelAr: 'سبب التخفيض' },
    { tag: '{requestedByName}', labelFr: 'Demandé Par', labelEn: 'Requested By', labelAr: 'طالب التخفيض' },
    { tag: '{currency}', labelFr: 'Devise', labelEn: 'Currency', labelAr: 'العملة' },
  ],
  DISCOUNT_REQUEST_APPROVED: [
    { tag: '{agencyName}', labelFr: 'Nom Agence', labelEn: 'Agency Name', labelAr: 'اسم الوكالة' },
    { tag: '{invoiceNumber}', labelFr: 'N° Facture', labelEn: 'Invoice Number', labelAr: 'رقم الفاتورة' },
    { tag: '{student.fullName}', labelFr: 'Nom Apprenant', labelEn: 'Student Full Name', labelAr: 'اسم التلميذ' },
    { tag: '{discountName}', labelFr: 'Nom Remise', labelEn: 'Discount Name', labelAr: 'اسم التخفيض' },
    { tag: '{discountPercent}', labelFr: 'Pourcentage Remise', labelEn: 'Discount Percent', labelAr: 'نسبة التخفيض' },
    { tag: '{newAmount}', labelFr: 'Nouveau Montant', labelEn: 'New Amount', labelAr: 'المبلغ الجديد' },
    { tag: '{reviewedByName}', labelFr: 'Validé Par', labelEn: 'Approved By', labelAr: 'تمت الموافقة بواسطة' },
    { tag: '{currency}', labelFr: 'Devise', labelEn: 'Currency', labelAr: 'العملة' },
  ],
  DISCOUNT_REQUEST_REJECTED: [
    { tag: '{agencyName}', labelFr: 'Nom Agence', labelEn: 'Agency Name', labelAr: 'اسم الوكالة' },
    { tag: '{invoiceNumber}', labelFr: 'N° Facture', labelEn: 'Invoice Number', labelAr: 'رقم الفاتورة' },
    { tag: '{student.fullName}', labelFr: 'Nom Apprenant', labelEn: 'Student Full Name', labelAr: 'اسم التلميذ' },
    { tag: '{discountName}', labelFr: 'Nom Remise', labelEn: 'Discount Name', labelAr: 'اسم التخفيض' },
    { tag: '{reviewedByName}', labelFr: 'Refusé Par', labelEn: 'Rejected By', labelAr: 'تم الرفض بواسطة' },
    { tag: '{reviewComment}', labelFr: 'Motif du Refus', labelEn: 'Rejection Reason', labelAr: 'سبب الرفض' },
    { tag: '{currency}', labelFr: 'Devise', labelEn: 'Currency', labelAr: 'العملة' },
  ],
  STUDENT_PRE_REG_ACCEPTED: [
    { tag: '{agencyName}', labelFr: 'Nom Agence', labelEn: 'Agency Name', labelAr: 'اسم الوكالة' },
    { tag: '{student.fullName}', labelFr: 'Nom Apprenant', labelEn: 'Student Full Name', labelAr: 'اسم التلميذ' },
    { tag: '{courseTitle}', labelFr: 'Titre de la Formation', labelEn: 'Course Title', labelAr: 'عنوان الدورة' },
    { tag: '{agencyPhone}', labelFr: 'Tél Agence', labelEn: 'Agency Phone', labelAr: 'هاتف الوكالة' },
    { tag: '{agencyEmail}', labelFr: 'Email Agence', labelEn: 'Agency Email', labelAr: 'بريد الوكالة' },
    { tag: '{agencyAddress}', labelFr: 'Adresse Agence', labelEn: 'Agency Address', labelAr: 'عنوان الوكالة' },
  ],
  STUDENT_PRE_REG_REFUSED: [
    { tag: '{agencyName}', labelFr: 'Nom Agence', labelEn: 'Agency Name', labelAr: 'اسم الوكالة' },
    { tag: '{student.fullName}', labelFr: 'Nom Apprenant', labelEn: 'Student Full Name', labelAr: 'اسم التلميذ' },
    { tag: '{agencyPhone}', labelFr: 'Tél Agence', labelEn: 'Agency Phone', labelAr: 'هاتف الوكالة' },
    { tag: '{agencyEmail}', labelFr: 'Email Agence', labelEn: 'Agency Email', labelAr: 'بريد الوكالة' },
  ],
  PAYMENT_RECEIPT: [
    { tag: '{agencyName}', labelFr: 'Nom Agence', labelEn: 'Agency Name', labelAr: 'اسم الوكالة' },
    { tag: '{student.fullName}', labelFr: 'Nom Apprenant', labelEn: 'Student Full Name', labelAr: 'اسم التلميذ' },
    { tag: '{paymentNumber}', labelFr: 'N° Paiement', labelEn: 'Payment Number', labelAr: 'رقم الدفعة' },
    { tag: '{paymentDate}', labelFr: 'Date Paiement', labelEn: 'Payment Date', labelAr: 'تاريخ الدفعة' },
    { tag: '{paymentAmount}', labelFr: 'Montant Payé', labelEn: 'Paid Amount', labelAr: 'المبلغ المدفوع' },
    { tag: '{paymentMethod}', labelFr: 'Mode de Règlement', labelEn: 'Payment Method', labelAr: 'طريقة الدفع' },
    { tag: '{invoiceNumber}', labelFr: 'N° Facture', labelEn: 'Invoice Number', labelAr: 'رقم الفاتورة' },
    { tag: '{restToPay}', labelFr: 'Reste à Payer', labelEn: 'Rest To Pay', labelAr: 'المبلغ المتبقي' },
    { tag: '{currency}', labelFr: 'Devise', labelEn: 'Currency', labelAr: 'العملة' },
  ],
  GENERAL: [
    { tag: '{agencyName}', labelFr: 'Nom Agence', labelEn: 'Agency Name', labelAr: 'اسم الوكالة' },
    { tag: '{agencyPhone}', labelFr: 'Téléphone Agence', labelEn: 'Agency Phone', labelAr: 'هاتف الوكالة' },
    { tag: '{agencyEmail}', labelFr: 'Email Agence', labelEn: 'Agency Email', labelAr: 'بريد الوكالة' },
    { tag: '{agencyAddress}', labelFr: 'Adresse Agence', labelEn: 'Agency Address', labelAr: 'عنوان الوكالة' },
    { tag: '{student.fullName}', labelFr: 'Nom Apprenant', labelEn: 'Student Full Name', labelAr: 'اسم التلميذ' },
  ]
};

// ==========================================
// SUBSCRIPTION & SATIM TYPES
// ==========================================
export type SubscriptionStatus = 'trial' | 'active' | 'expired' | 'canceled' | 'suspended';
export type BillingCycle = 'monthly' | 'yearly';
export type SubscriptionPaymentMethod = 'SATIM_CIB' | 'SATIM_EDAHABIA' | 'SATIM' | 'CCP' | 'BANK_TRANSFER' | 'CASH';

export interface AcademySubscriptionPlan {
  PLN_IDF: number;
  PLN_CODE: string;
  PLN_NAME: string;
  PLN_DESCRIPTION?: string;
  PLN_PRICE_MONTHLY: number;
  PLN_PRICE_YEARLY: number;
  PLN_CURRENCY: string;
  PLN_TRIAL_DAYS: number;
  PLN_MAX_STUDENTS: number;
  PLN_MAX_TEACHERS: number;
  PLN_MAX_AGENCIES: number;
  PLN_MAX_USERS: number;
  PLN_SMS_CREDITS: number;
  PLN_STORAGE_MB: number;
  PLN_REPORTS_ENABLED: boolean;
  PLN_FEATURES_JSON?: string;
  PLN_BADGE?: string;
  PLN_COLOR?: string;
  PLN_ORDER: number;
  PLN_IS_ACTIVE: boolean;
}

export interface AcademySubscription {
  SUB_IDF: number;
  DOS_IDF: number;
  DOS_CODE?: string;
  DOS_LIB?: string;
  PLN_IDF: number;
  PLN_CODE?: string;
  PLN_NAME?: string;
  SUB_STATUS: SubscriptionStatus;
  SUB_BILLING_CYCLE: BillingCycle;
  SUB_START_DATE: string;
  SUB_END_DATE: string;
  SUB_TRIAL_END_DATE?: string;
  SUB_AUTO_RENEW: boolean;
  SUB_PAYMENT_METHOD?: string;
  SUB_AMOUNT_PAID: number;
  SUB_CURRENCY: string;
  SUB_NOTES?: string;
  LAST_UPDT?: string;
  USER_IDF?: number;
  USER_LIB?: string;
}

export interface AcademySubscriptionInvoice {
  INV_IDF: number;
  SUB_IDF: number;
  DOS_IDF: number;
  DOS_CODE?: string;
  DOS_LIB?: string;
  INV_NUMBER: string;
  INV_DATE: string;
  INV_DUE_DATE?: string;
  INV_AMOUNT: number;
  INV_CURRENCY: string;
  INV_STATUS: 'PAID' | 'PENDING' | 'OVERDUE' | 'CANCELED';
  INV_PAYMENT_METHOD?: string;
  INV_TRANSACTION_ID?: string;
  INV_NOTES?: string;
  LAST_UPDT?: string;
}

export interface SubscriptionUsage {
  dosId: number;
  subscriptionId?: number;
  planId: number;
  planCode: string;
  planName: string;
  status: SubscriptionStatus | 'none';
  billingCycle: BillingCycle;
  startDate?: string;
  endDate?: string;
  trialEndDate?: string;
  autoRenew?: boolean;
  paymentMethod?: string;
  studentsUsed: number;
  studentsMax: number;
  teachersUsed: number;
  teachersMax: number;
  usersUsed: number;
  usersMax: number;
  agenciesUsed: number;
  agenciesMax: number;
  storageUsedMb: number;
  storageMaxMb: number;
  smsUsed: number;
  smsMax: number;
  reportsEnabled?: boolean;
  featuresJson?: string;
  planBadge?: string;
  planColor?: string;
}

export interface SatimInitiateResponse {
  orderId: string;
  orderNumber: string;
  formUrl: string;
  amount: number;
  isTestMode: boolean;
  planId: number;
  planName: string;
  billingCycle: string;
}

export interface SatimConfirmResponse {
  success: boolean;
  orderId: string;
  approvalCode: string;
  cardBrand: string;
  cardNumberMasked: string;
  upgrade?: any;
}

// ============================================================================
// ACADEMY SESSIONS & ATTENDANCE
// ============================================================================
export interface AcademySession {
  id: number;
  uid?: string;
  grp_idf: number;
  group_id?: number;
  group_name?: string;
  tch_idf: number;
  teacher_name?: string;
  rom_idf?: number;
  room_name?: string;
  date: string;
  start_time: string;
  end_time: string;
  topic?: string;
  status: 'SCHEDULED' | 'COMPLETED' | 'CANCELLED' | 'POSTPONED';
  status_reason?: string;
  postponed_date?: string;
  postponed_time?: string;
  teacher_absent?: boolean;
  attendance_taken?: boolean;
  attendances?: any[];
  notes?: string;
  dos_idf?: number;
}

export interface AcademyAttendance {
  id: number;
  uid?: string;
  ssn_idf: number;
  std_idf: number;
  student_name?: string;
  student_code?: string;
  status: 'PRESENT' | 'ABSENT' | 'LATE' | 'EXCUSED';
  arrival_time?: string;
  scan_type?: 'MANUAL' | 'BARCODE' | 'QR';
  scanned_at?: string;
  justified?: boolean;
  justification_reason?: string;
  justification_doc_name?: string;
  justification_doc_base64?: string;
  justification_date?: string;
  notes?: string;
  dos_idf?: number;
}

export interface TeacherAbsenceRequest {
  id: number;
  uid?: string;
  tch_idf: number;
  teacher_name?: string;
  ssn_idf?: number;
  request_date?: string;
  start_date: string;
  end_date?: string;
  reason: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  admin_comment?: string;
  attachment_name?: string;
  attachment_base64?: string;
  decision_date?: string;
  dos_idf?: number;
}

// ============================================================================
// ACADEMY PORTALS DTOs
// ============================================================================
export interface PortalAcademy {
  dos_idf: number;
  dos_code: string;
  dos_lib: string;
  address?: string;
  phone?: string;
  logo_base64?: string;
}

export interface PortalStudentLoginResponse {
  token: string;
  student: Student;
  academies: PortalAcademy[];
  selected_academy?: PortalAcademy;
  requires_academy_selection: boolean;
}

export interface PortalTeacherLoginResponse {
  token: string;
  teacher: Teacher;
  academies: PortalAcademy[];
  selected_academy?: PortalAcademy;
  requires_academy_selection: boolean;
}

export interface PortalStudentData {
  student: Student;
  category: 'adult' | 'junior';
  children?: Student[];
  currentChild?: Student;
  groups: any[];
  sessions: AcademySession[];
  attendances: AcademyAttendance[];
  payments: any[];
  disruptions: AcademySession[];
  academy: PortalAcademy;
}

export interface PortalTeacherData {
  teacher: Teacher;
  can_take_attendance: boolean;
  groups: any[];
  sessions: AcademySession[];
  upcoming_sessions: AcademySession[];
  absence_requests: TeacherAbsenceRequest[];
  finances: {
    rate_type: string;
    hourly_rate: number;
    total_hours_month: number;
    total_amount_month: number;
    history: any[];
  };
  academy: PortalAcademy;
}


